import { useCallback, useEffect, useState } from 'react';
import { getAccessToken } from '../utils/auth';
import { getAirlineGaps, upsertAirline, uploadAirlineLogo } from '../utils/httpApi';
import { isApiSuccess, getFirstApiError } from '../utils/api';
import { fileToBase64 } from '../utils/fileToBase64';
import { loadAirlineLogoMap } from '../utils/airlineLogoMap';
import './Admin.css';

const EMPTY_FORM = {
  icaoCode: '',
  iataCode: '',
  airlineName: '',
  callsign: '',
  country: '',
  countryCode: '',
  isMilitary: false,
  logoUrl: '',
  resolveMissingCode: true
};

function AdminAirlineGaps() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingKey, setEditingKey] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [logoFile, setLogoFile] = useState(null);
  const [actionMsg, setActionMsg] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = getAccessToken();
      const resp = await getAirlineGaps({ includeMissingLogos: true, includeMissingCodes: true, limit: 200 }, token);
      if (isApiSuccess(resp)) {
        setRows(resp.rows || []);
      } else {
        setError(getFirstApiError(resp) || 'Failed to load airline gaps');
      }
    } catch (e) {
      setError('Failed to load airline gaps');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startEdit = (row) => {
    const key = `${row.source}:${row.icaoCode || row.iataCode}`;
    setEditingKey(key);
    setForm({
      icaoCode: row.icaoCode || '',
      iataCode: row.iataCode || '',
      airlineName: row.airlineName || '',
      callsign: '',
      country: row.country || '',
      countryCode: row.countryCode || '',
      isMilitary: false,
      logoUrl: row.logoUrl || '',
      resolveMissingCode: row.source === 'MISSING_CODE'
    });
    setLogoFile(null);
    setActionMsg(null);
  };

  const save = async () => {
    if (!form.icaoCode) {
      setActionMsg({ type: 'error', text: 'ICAO code is required' });
      return;
    }
    try {
      const token = getAccessToken();
      const upsertResp = await upsertAirline({
        icaoCode: form.icaoCode.toUpperCase(),
        iataCode: form.iataCode || undefined,
        airlineName: form.airlineName || undefined,
        callsign: form.callsign || undefined,
        country: form.country || undefined,
        countryCode: form.countryCode || undefined,
        isMilitary: form.isMilitary,
        resolveMissingCode: form.resolveMissingCode
      }, token);
      if (!isApiSuccess(upsertResp)) {
        setActionMsg({ type: 'error', text: getFirstApiError(upsertResp) || 'Save failed' });
        return;
      }
      if (logoFile) {
        const base64 = await fileToBase64(logoFile);
        const logoResp = await uploadAirlineLogo({
          icaoCode: form.icaoCode.toUpperCase(),
          fileName: logoFile.name,
          fileBase64: base64
        }, token);
        if (!isApiSuccess(logoResp)) {
          setActionMsg({ type: 'error', text: getFirstApiError(logoResp) || 'Logo upload failed' });
          return;
        }
      }
      setActionMsg({ type: 'success', text: `Saved ${form.icaoCode}` });
      setEditingKey(null);
      if (logoFile) loadAirlineLogoMap();
      load();
    } catch (e) {
      setActionMsg({ type: 'error', text: 'Save failed' });
    }
  };

  if (loading) return <div className="admin-loading">Loading airline gaps...</div>;
  if (error) {
    return (
      <div className="admin-error">
        <p>{error}</p>
        <button onClick={load}>Retry</button>
      </div>
    );
  }

  return (
    <div className="admin-table-container">
      <div className="admin-toolbar">
        <button onClick={load}>Refresh</button>
        <span className="admin-toolbar-meta">{rows.length} airlines with gaps</span>
      </div>

      {actionMsg && (
        <div className={`admin-action-message admin-action-${actionMsg.type}`}>
          {actionMsg.text}
        </div>
      )}

      <table className="admin-table">
        <thead>
          <tr>
            <th>Source</th>
            <th>ICAO</th>
            <th>IATA</th>
            <th>Name</th>
            <th>Country</th>
            <th>Logo</th>
            <th>Sightings</th>
            <th>Sample callsigns</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {rows.map(row => {
            const key = `${row.source}:${row.icaoCode || row.iataCode}`;
            const isEditing = editingKey === key;
            const sourceLabel = row.source === 'MISSING_CODE' ? 'Unknown code' : 'Missing logo';
            return (
              <>
                <tr key={key} className={isEditing ? 'expanded' : ''}>
                  <td data-label="Source">
                    <span className={`admin-status-badge ${row.source === 'MISSING_CODE' ? 'inactive' : 'active'}`}>
                      {sourceLabel}
                    </span>
                  </td>
                  <td data-label="ICAO">{row.icaoCode || <span className="gap">—</span>}</td>
                  <td data-label="IATA">{row.iataCode || <span className="gap">—</span>}</td>
                  <td data-label="Name">{row.airlineName || <span className="gap">—</span>}</td>
                  <td data-label="Country">{row.country || <span className="gap">—</span>}</td>
                  <td data-label="Logo">
                    {row.logoUrl ? <img src={row.logoUrl} alt="" style={{ height: 24, maxWidth: 80, objectFit: 'contain' }} />
                      : <span className="gap">—</span>}
                  </td>
                  <td data-label="Sightings">{row.sightingCount || ''}</td>
                  <td data-label="Sample">{row.sampleCallsigns || ''}</td>
                  <td>
                    <button onClick={() => isEditing ? setEditingKey(null) : startEdit(row)}>
                      {isEditing ? 'Cancel' : 'Edit'}
                    </button>
                  </td>
                </tr>
                {isEditing && (
                  <tr key={`${key}-edit`} className="receivers-row">
                    <td colSpan={9}>
                      <div className="admin-edit-form">
                        <label>ICAO code<input value={form.icaoCode} maxLength={3} onChange={e => setForm({ ...form, icaoCode: e.target.value.toUpperCase() })} /></label>
                        <label>IATA code<input value={form.iataCode} maxLength={3} onChange={e => setForm({ ...form, iataCode: e.target.value.toUpperCase() })} /></label>
                        <label>Airline name<input value={form.airlineName} onChange={e => setForm({ ...form, airlineName: e.target.value })} /></label>
                        <label>Callsign<input value={form.callsign} onChange={e => setForm({ ...form, callsign: e.target.value })} /></label>
                        <label>Country<input value={form.country} onChange={e => setForm({ ...form, country: e.target.value })} /></label>
                        <label>Country code<input value={form.countryCode} maxLength={2} onChange={e => setForm({ ...form, countryCode: e.target.value.toUpperCase() })} /></label>
                        <label className="admin-edit-form-checkbox">
                          <input type="checkbox" checked={form.isMilitary} onChange={e => setForm({ ...form, isMilitary: e.target.checked })} />
                          Military
                        </label>
                        <label className="admin-edit-form-wide">
                          Logo (PNG / JPG / SVG; PNG/JPG backgrounds auto-removed)
                          <input type="file" accept="image/*" onChange={e => setLogoFile(e.target.files && e.target.files[0])} />
                        </label>
                        {row.source === 'MISSING_CODE' && (
                          <label className="admin-edit-form-checkbox">
                            <input type="checkbox" checked={form.resolveMissingCode} onChange={e => setForm({ ...form, resolveMissingCode: e.target.checked })} />
                            Mark unknown code as resolved
                          </label>
                        )}
                        <div className="admin-edit-form-actions">
                          <button onClick={save}>Save</button>
                          <button onClick={() => setEditingKey(null)}>Cancel</button>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default AdminAirlineGaps;
